import axios from 'axios';

import type { PriceBookPinnedBy } from './patch-price-pin';

/** Why the resolver landed where it did. 'applied'/'frozen' mean a targeting rule chose the version. */
export type PriceBookTargetingStatus = 'locked' | 'no-rules' | 'no-deal' | 'not-observed' | 'stale' | 'no-match' | 'applied' | 'frozen';

export type DesignPriceBookResolution = {
	/** The version this design prices against. Null means no version is published — price on the live catalog. */
	effectiveVersionId: string | null;
	pinnedBy: PriceBookPinnedBy | null;
	effectiveRuleId: string | null;
	/** A version a rule would move the design to, which the designer applies rather than it moving on its own. */
	candidateVersionId: string | null;
	observedProperty: string;
	observedValue: string;
	targetingStatus: PriceBookTargetingStatus;
	version: number | null;
};

export type FetchDesignPriceBookParams = {
	email: string,
	designName: string,
	/** kitchen version to resolve for; null follows the active version */
	version?: number | null,
	getToken: () => Promise<string>
};

/**
 * The server's own answer to "which price book does this design price against", targeting rules
 * included. The browser cannot evaluate rules — deal properties never reach it — so this is the only
 * way the chip and the design package agree on a rule-matched version.
 * @returns the resolution, or [null, null] when the design is not stored yet and there is nothing to resolve
 */
export const fetchDesignPriceBook = async (params: FetchDesignPriceBookParams): Promise<[DesignPriceBookResolution | null, string | null]> => {
	const { email, designName, version = null, getToken } = params;

	try {
		const token = await getToken();
		const query = version === null ? '' : `?version=${version}`;
		const response = await axios.get<DesignPriceBookResolution>(
			`${import.meta.env.VITE_API_URL}/customers/${encodeURIComponent(email)}/blueprints/${encodeURIComponent(designName)}/price-book${query}`,
			{ headers: { Authorization: `Bearer ${token}` } }
		);
		return [response.data, null];
	} catch (error) {
		// 404 is "this design is not in the blob yet", which is nothing to resolve rather than a fault.
		if (axios.isAxiosError(error) && error.response?.status === 404) return [null, null];
		return [null, 'Failed to resolve the price book for this design'];
	}
};
